import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, clearAuthSession, setCsrfToken } from '../lib/apiClient';
import { API_ROUTES } from '../lib/apiRoutes';

const AuthContext = createContext(null);

function normalizeUser(data) {
  if (!data) return null;
  return {
    ...data,
    isTrialActive: data.is_trial_active,
    trialDaysRemaining: data.trial_days_remaining,
    effectivePlan: data.effective_plan,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const authRevisionRef = useRef(0);

  const logout = useCallback(async () => {
    authRevisionRef.current += 1;
    try {
      await apiRequest(API_ROUTES.auth.logout, { method: 'POST' });
    } catch {
      // Clearing local state is still required if the API is unavailable.
    }
    clearAuthSession();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const revision = authRevisionRef.current;
      try {
        const data = await apiRequest(API_ROUTES.auth.me);
        if (authRevisionRef.current !== revision) return;
        setUser(normalizeUser(data));
        setToken(null);
      } catch (e) {
        if (authRevisionRef.current !== revision) return;
        if (!navigator.onLine || e.message === 'Failed to fetch') return;
        setUser(null);
        setToken(null);
      } finally {
        if (authRevisionRef.current === revision) setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const login = useCallback((accessToken, userData, csrf) => {
    authRevisionRef.current += 1;
    if (accessToken) localStorage.setItem('access_token', accessToken);
    else localStorage.removeItem('access_token');
    if (csrf) setCsrfToken(csrf);
    setToken(accessToken || null);
    setUser(normalizeUser(userData));
    setLoading(false);
  }, []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
